/**
 * cardComments — the take preview on a CARD reads the real thread.
 *
 * WHAT WAS WRONG. GET /api/listings returns no comments and no count, so
 * floorTransport maps every row to `comments: [] / commentCount: 0` (see its
 * header) and every real listing's card said "No takes yet. Start the thread."
 * with a 0 beside the comment glyph, whatever the thread actually held. The
 * detail page has fetched the real thread since installLiveListingComments; the
 * card never did, because recon 15 §3 priced one call per grid row (100 rows on
 * the floor then, every active listing since 2026-09-27) as too much to do up
 * front, all at once, for every visitor.
 *
 * WHAT THIS DOES. The same per-card GET /api/comments/:id the app's
 * ListingCardV2 makes for every feed card (on mount, ungated) — bounded here by
 * VISIBILITY: a card's thread is fetched the first time that card scrolls into
 * view, once per listing per page load, at most MAX_IN_FLIGHT at a time. A
 * visitor who never scrolls past the first screen costs the first screen.
 *
 * 🔑 PATCHES THE CARD IN PLACE, NEVER window.render(). A full repaint on every
 * resolved fetch would fire eight times over a freshly painted floor, drop the
 * caret out of a composer someone is typing in, and fight the scroll position.
 * The thread lands on the LISTINGS row through __gbzSetListingComments — so
 * every LATER render paints it from data, the way renderCard always did — and
 * only this card's `.take-top` and its glyph count are rewritten now, through
 * window.renderTakeTop: app.js's OWN renderer for that block, the one renderCard
 * and the tour use. The markup is never re-implemented here.
 *
 * VIEW ALL inside a patched block keeps working because app.js binds
 * [data-comments] by delegation on the document, not per element in wire().
 *
 * AUTHORS FIRST, COMMENTS SECOND — the ordering installLiveListingComments
 * documents: the patch reads USERS for the photo and the handle.
 *
 * Best-effort like every lazy installer: a failed fetch leaves the honest empty
 * state, and is not retried this page load (the detail loader's posture — a
 * thread that 500s once should not 500 on every scroll). An empty thread paints
 * nothing, because the empty state already on the card is the truth.
 *
 * 🪤 A card can hold a listing whose thread is ALREADY on the row — the detail
 * page loaded it, or the visitor just posted from this card and commentPost
 * re-fetched it. That row is not fetched again, but it IS painted: the card on
 * screen may have been drawn before the row filled (Back from a detail page
 * whose read landed after the repaint), and skipping the paint left it saying
 * "No takes yet" over a thread this module was holding.
 *
 * 🪤 A RESPONSE CAN BE OLDER THAN THE ROW. A post from the card re-reads the
 * thread while this module's read of the same listing may still be in flight;
 * the later-arriving, earlier-issued response would overwrite the visitor's own
 * take. A write replaces the row's array, so a changed array means drop.
 *
 * NOT EVERY CARD IS A THREAD. Collection pieces carry user_collections ids and
 * the guided tour mints fixture ids under "tour-"; the comments route answers
 * 200 [] for any string, so each was a wasted round trip against a guest's
 * rate-limit budget. Neither is fetched.
 */

import { mapCommentRow, mapCommentAuthors, type ApiCommentRow, type DemoComment, type DemoUser } from "./floorTransport";

/** Blake's card, keyed by the listing id renderCard puts on it (app.js renderCard). */
export const CARD_SELECTOR = ".lcard[data-listing]";
/** The top half of the take block, the part renderTakeTop paints. */
export const TAKE_TOP_SELECTOR = ".take-top";
/** The comment glyph: an svg and the count as its trailing text node. */
/* By class, not by attribute: the glyph's attribute is what its tap does (it opens
   the pop-up or focuses the composer), and [data-comments] would also match VIEW
   ALL inside the take block. */
export const COUNT_SELECTOR = ".social-comment";
/** Concurrent thread fetches. Four is a screenful arriving together without
 *  a burst of a dozen when a fast scroll flips several rows into view. */
export const MAX_IN_FLIGHT = 4;
/** Start a card's fetch a little before it scrolls in, so the preview is
 *  usually there when the eye gets to it. */
export const ROOT_MARGIN = "200px 0px";
/** The guided tour's fixture namespace — the same prefix app.js's own
 *  tourRefreshFixtures checks. Fixture ids exist in no table. */
export const TOUR_ID_PREFIX = "tour-";

interface ListingRowView {
  comments?: unknown[];
  commentCount?: number;
  /** A collection piece: its id is a user_collections id, not a listing's. */
  collectionOnly?: boolean;
}

interface CardWindow extends Window {
  __gbzListingRow?: (id: string) => ListingRowView | null;
  __gbzSetListingComments?: (id: string, comments: DemoComment[]) => boolean;
  __gbzMergeUsers?: (users: Record<string, DemoUser>) => number;
  /** app.js's top-level function — a window property because app.js is a
   *  classic script, the same way window.render is. */
  renderTakeTop?: (row: unknown) => string;
  /** app.js's accessible name for the comment glyph, which carries the count. */
  takeCountLabel?: (n: number) => string;
}

export function installCardComments(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /** installFloorListings' handle→id map, mutated in place as authors arrive —
   *  the same object installLiveListingComments and installRatingFixups hold. */
  userIds?: Record<string, string>,
): () => void {
  const target = win as CardWindow;
  const rawSet = target.__gbzSetListingComments;
  if (typeof rawSet !== "function") {
    throw new Error("installCardComments: bridge script did not define window.__gbzSetListingComments");
  }
  const rawRow = target.__gbzListingRow;
  if (typeof rawRow !== "function") {
    throw new Error("installCardComments: bridge script did not define window.__gbzListingRow");
  }
  const setComments: (id: string, comments: DemoComment[]) => boolean = rawSet;
  const listingRow: (id: string) => ListingRowView | null = rawRow;

  const IO = (win as unknown as { IntersectionObserver?: typeof IntersectionObserver }).IntersectionObserver;
  if (typeof IO !== "function") {
    // No visibility signal (an old engine): the honest empty state stays, as it
    // did before this module existed. Never "fetch everything" as a fallback —
    // that is the up-front cost this module exists to avoid.
    return () => {};
  }

  /** Listing ids handled this page load: queued, fetched, failed, or skipped. */
  const seen = new Set<string>();
  const queued: string[] = [];
  let inFlight = 0;

  function cardsFor(id: string): HTMLElement[] {
    return Array.from(win.document.querySelectorAll<HTMLElement>(CARD_SELECTOR)).filter((c) => c.dataset.listing === id);
  }

  /** Rewrite this listing's card(s) from the row app.js now holds. */
  function paint(id: string): void {
    const row = listingRow(id);
    const renderTop = target.renderTakeTop;
    if (!row || typeof renderTop !== "function") return;
    const html = renderTop(row);
    const count = String(row.commentCount ?? 0);
    for (const card of cardsFor(id)) {
      const top = card.querySelector(TAKE_TOP_SELECTOR);
      if (top) top.innerHTML = html;
      const glyph = card.querySelector(COUNT_SELECTOR);
      if (glyph) {
        const text = Array.from(glyph.childNodes).reverse().find((n) => n.nodeType === 3);
        if (text) text.nodeValue = count;
        const label = target.takeCountLabel;
        if (typeof label === "function") glyph.setAttribute("aria-label", label(row.commentCount ?? 0));
      }
    }
  }

  async function load(id: string): Promise<void> {
    try {
      // Filled while it waited in the queue (a post, the detail page): paint, don't ask.
      const queuedRow = listingRow(id);
      if (queuedRow && Array.isArray(queuedRow.comments) && queuedRow.comments.length > 0) {
        paint(id);
        return;
      }
      /* The row's array as this read is issued. Any write that lands while it is
         in flight replaces that array (__gbzSetListingComments assigns a new one)
         and is newer than this response — see the header. */
      const before = queuedRow?.comments;
      const res = await authFetch(`/api/comments/${encodeURIComponent(id)}`);
      if (!res.ok) return;
      const rows = (await res.json()) as ApiCommentRow[];
      if (!Array.isArray(rows) || rows.length === 0) return;
      if (listingRow(id)?.comments !== before) return;
      const authors = mapCommentAuthors(rows);
      if (userIds) {
        for (const handle of Object.keys(authors)) {
          const uid = authors[handle].id;
          if (uid && !userIds[handle]) userIds[handle] = uid;
        }
      }
      target.__gbzMergeUsers?.(authors);
      if (setComments(id, rows.map((r) => mapCommentRow(r)))) paint(id);
    } catch {
      // the honest empty state stays; not retried this page load
    }
  }

  function pump(): void {
    while (inFlight < MAX_IN_FLIGHT && queued.length) {
      const id = queued.shift()!;
      inFlight++;
      void load(id).finally(() => {
        inFlight--;
        pump();
      });
    }
  }

  function want(id: string): void {
    if (seen.has(id)) return;
    seen.add(id);
    const row = listingRow(id);
    // Not a thread anyone can have: no floor row, a collection piece, a tour fixture.
    if (!row || row.collectionOnly === true || id.indexOf(TOUR_ID_PREFIX) === 0) return;
    // Already holding the thread: no fetch, but paint — the card may predate the row.
    if (Array.isArray(row.comments) && row.comments.length > 0) {
      paint(id);
      return;
    }
    queued.push(id);
    pump();
  }

  const io = new IO((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      io.unobserve(entry.target);
      const id = (entry.target as HTMLElement).dataset.listing;
      if (id) want(id);
    }
  }, { rootMargin: ROOT_MARGIN });

  /** Cards this observer already watches — a repaint replaces nodes, so this
   *  is per node, and a re-rendered card of a seen listing is simply skipped. */
  const observed = new WeakSet<Element>();
  function observeCards(): void {
    for (const card of Array.from(win.document.querySelectorAll<HTMLElement>(CARD_SELECTOR))) {
      const id = card.dataset.listing;
      if (!id || seen.has(id) || observed.has(card)) continue;
      observed.add(card);
      io.observe(card);
    }
  }

  /* Same posture as listingLikes and followUser: his bundle replaces #app on
     every render, so the cards have to be re-found, not found. */
  const MO = (win as unknown as { MutationObserver: typeof MutationObserver }).MutationObserver;
  const mo = new MO(observeCards);
  const root = win.document.getElementById("app") ?? win.document.body;
  if (root) mo.observe(root, { childList: true, subtree: true });
  observeCards();

  return () => {
    mo.disconnect();
    io.disconnect();
  };
}
